// Сотрудники: типы, CRUD, увольнение, дубли (выделено из reference.api.ts планом 108, Ф4).
import { api } from '@/lib/api';

import { defined } from './_shared.js';

export type EmployeeRole =
  | 'boss'
  | 'manager'
  | 'senior_admin'
  | 'admin'
  | 'sales_manager'
  | 'teacher';
/** Вторая роль при совмещении (boss второй не бывает). */
export type SecondaryEmployeeRole = Exclude<EmployeeRole, 'boss'>;
/** Статус сотрудника. «Архив» упразднён 2026-07-20 (docs/service/33): осталось два. */
export type EmployeeStatus = 'active' | 'fired';

export interface Employee {
  id: string;
  role: EmployeeRole;
  secondaryRole: SecondaryEmployeeRole | null;
  firstName: string;
  lastName: string;
  middleName: string | null;
  phone: string | null;
  email: string | null;
  birthDate: string | null;
  hiredAt: string | null;
  gender: 'm' | 'f' | null;
  status: EmployeeStatus;
  // E2: профиль педагога.
  description: string | null;
  photoUrl: string | null;
  /**
   * Адреса в мессенджерах (план 107) — вводятся руками. Внешний поиск
   * «НайтиЧат» для сотрудников не подключён, числовых id мы не показываем.
   */
  tgUsername: string | null;
  waPhone: string | null;
  vkLink: string | null;
  branchIds: string[];
  directionIds: string[];
}

// --- Employees (Mobifitness «Педагоги/Сотрудники») — employees-модуль -------
// Ответы camelCase; тела запросов snake_case (first_name, birth_date…).

export interface CreateEmployeeBody {
  role: EmployeeRole;
  secondaryRole?: SecondaryEmployeeRole | null;
  firstName: string;
  lastName: string;
  middleName?: string | null;
  phone?: string | null;
  email?: string | null;
  birthDate?: string | null;
  hiredAt?: string | null;
  gender?: 'm' | 'f' | null;
  // E2: профиль педагога.
  description?: string | null;
  branchIds?: string[];
  directionIds?: string[];
  // План 107: адреса мессенджеров.
  tgUsername?: string | null;
  waPhone?: string | null;
  vkLink?: string | null;
}

export interface UpdateEmployeeBody {
  role?: EmployeeRole;
  secondaryRole?: SecondaryEmployeeRole | null;
  firstName?: string;
  lastName?: string;
  middleName?: string | null;
  phone?: string | null;
  email?: string | null;
  birthDate?: string | null;
  hiredAt?: string | null;
  gender?: 'm' | 'f' | null;
  status?: EmployeeStatus;
  // E2: профиль педагога.
  description?: string | null;
  branchIds?: string[];
  directionIds?: string[];
  // План 107: адреса мессенджеров.
  tgUsername?: string | null;
  waPhone?: string | null;
  vkLink?: string | null;
}

export interface ListEmployeesParams {
  role?: EmployeeRole;
  includeArchived?: boolean;
}

export function listEmployees(params: ListEmployeesParams = {}): Promise<{ items: Employee[] }> {
  const sp = new URLSearchParams();
  if (params.role) sp.set('role', params.role);
  if (params.includeArchived) sp.set('include_archived', 'true');
  const qs = sp.toString();
  return api.get<{ items: Employee[] }>(`/employees${qs ? `?${qs}` : ''}`);
}

export function createEmployee(body: CreateEmployeeBody): Promise<{ employee: Employee }> {
  return api.post<{ employee: Employee }>(
    '/employees',
    defined({
      role: body.role,
      secondary_role: body.secondaryRole,
      first_name: body.firstName,
      last_name: body.lastName,
      middle_name: body.middleName,
      phone: body.phone,
      email: body.email,
      birth_date: body.birthDate,
      hired_at: body.hiredAt,
      gender: body.gender,
      description: body.description,
      branch_ids: body.branchIds,
      direction_ids: body.directionIds,
      tg_username: body.tgUsername,
      wa_phone: body.waPhone,
      vk_link: body.vkLink,
    }),
  );
}

export function updateEmployee(
  id: string,
  body: UpdateEmployeeBody,
): Promise<{ employee: Employee }> {
  return api.patch<{ employee: Employee }>(
    `/employees/${id}`,
    defined({
      role: body.role,
      secondary_role: body.secondaryRole,
      first_name: body.firstName,
      last_name: body.lastName,
      middle_name: body.middleName,
      phone: body.phone,
      email: body.email,
      birth_date: body.birthDate,
      hired_at: body.hiredAt,
      gender: body.gender,
      status: body.status,
      description: body.description,
      branch_ids: body.branchIds,
      direction_ids: body.directionIds,
      tg_username: body.tgUsername,
      wa_phone: body.waPhone,
      vk_link: body.vkLink,
    }),
  );
}

/** Что останется висеть на сотруднике при увольнении (docs/service/33). */
export interface OffboardingSummary {
  open_shift: { id: string; branch_id: string; branch_name: string; opened_at: string } | null;
  future_occurrences_count: number;
  clients_count: number;
  open_tasks_count: number;
  group_links_count: number;
}

export function getOffboardingSummary(id: string): Promise<OffboardingSummary> {
  return api
    .get<{ summary: OffboardingSummary }>(`/employees/${id}/offboarding-summary`)
    .then((r) => r.summary);
}

export interface DismissEmployeeBody {
  dismissed_at?: string;
  reassign_occurrences_to?: string;
  reassign_clients_to?: string;
  reassign_tasks_to?: string;
  close_open_shift?: boolean;
}

export interface DismissEmployeeResult {
  employee_id: string;
  fired_at: string;
  reassigned_occurrences: number;
  reassigned_clients: number;
  reassigned_tasks: number;
  closed_shift: boolean;
}

/** Увольнение с передачей дел — одной операцией. */
export function dismissEmployee(
  id: string,
  body: DismissEmployeeBody,
): Promise<DismissEmployeeResult> {
  return api
    .post<{ result: DismissEmployeeResult }>(`/employees/${id}/dismiss`, body)
    .then((r) => r.result);
}

export function archiveEmployee(id: string): Promise<void> {
  return api.delete<void>(`/employees/${id}`);
}

/**
 * E2: загрузка аватара педагога (multipart/form-data). Возвращает проксирующий
 * photo_url (/api/v1/employees/:id/photo). Файл уходит в поле `file`.
 */
export function uploadEmployeePhoto(id: string, file: File): Promise<{ photo_url: string }> {
  const fd = new FormData();
  fd.append('file', file);
  return api.post<{ photo_url: string }>(`/employees/${id}/photo`, fd);
}

// --- Объединение дублей сотрудников (план 68 П2) ---------------------------

export interface EmployeeDuplicateMember {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  role: EmployeeRole;
  /** Дата приёма; по ней предлагаем победителя — у кого стаж длиннее. */
  hiredAt: string | null;
  /** 'active' | 'fired': уволенный может быть только проигравшим (план 82). */
  status: string;
}

export interface EmployeeDuplicateCluster {
  reason: 'phone' | 'name';
  key: string;
  employees: EmployeeDuplicateMember[];
}

export function scanEmployeeDuplicates(): Promise<{ clusters: EmployeeDuplicateCluster[] }> {
  return api.get<{ clusters: EmployeeDuplicateCluster[] }>('/employees/duplicates/scan');
}

/** `winnerId` остаётся, `loserId` уходит в уволенные с указателем на победителя. */
export function mergeEmployees(
  winnerId: string,
  loserId: string,
  reason?: string,
): Promise<unknown> {
  return api.post(`/employees/${winnerId}/merge`, {
    loser_employee_id: loserId,
    ...(reason ? { reason } : {}),
  });
}

/**
 * Ответственный по умолчанию для сделок ГОРОДА (план 104 Б7). Настройка висит
 * на городе, а не на филиале: у Мурманска два зала и один менеджер, а лид
 * часто приходит без локации — филиала тогда нет, а город известен.
 */
export function setCityDefaultResponsible(
  cityId: string,
  employeeId: string | null,
): Promise<{ ok: boolean }> {
  return api.patch<{ ok: boolean }>(`/branches/cities/${cityId}`, {
    default_deal_responsible_employee_id: employeeId,
  });
}
